/**
 * Multi-channel Campaign Creation Wizard.
 *
 * Starts with channel selection (Meta / Google), then shows channel-specific
 * steps.  Meta: 4-step wizard (setup, audience, creative, review).
 * Google: simplified flow (name, type, budget, bidding).
 */

import { useState, useRef, useCallback } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Input,
  Textarea,
  Spinner,
} from '@/components/ui'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui'
import { ArrowLeft, ArrowRight, Rocket, Upload, X, ImageIcon } from 'lucide-react'
import { useMarketing } from '@/hooks/useMarketing'
import { useMarketingStore } from '@/stores/marketingStore'
import { useToast } from '@/components/ui/Toast'
import type { CampaignCreateRequest, AdSetCreateRequest, AdCreateRequest, MarketingChannel } from '@/types/marketing'

interface WizardProps {
  onClose: () => void
  onCreated: () => void
}

const META_OBJECTIVES = [
  { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement (CTWA)' },
  { value: 'OUTCOME_LEADS', label: 'Lead Generation' },
  { value: 'OUTCOME_SALES', label: 'Sales' },
  { value: 'OUTCOME_AWARENESS', label: 'Brand Awareness' },
  { value: 'OUTCOME_TRAFFIC', label: 'Website Traffic' },
]

// Keep backwards compat
const OBJECTIVES = META_OBJECTIVES

const GOOGLE_CAMPAIGN_TYPES = [
  { value: 'SEARCH', label: 'Search' },
  { value: 'DISPLAY', label: 'Display' },
  { value: 'PERFORMANCE_MAX', label: 'Performance Max' },
  { value: 'DEMAND_GEN', label: 'Demand Gen' },
]

const GOOGLE_BIDDING_STRATEGIES = [
  { value: 'MAXIMIZE_CONVERSIONS', label: 'Maximize Conversions' },
  { value: 'MAXIMIZE_CLICKS', label: 'Maximize Clicks' },
  { value: 'TARGET_CPA', label: 'Target CPA' },
  { value: 'TARGET_ROAS', label: 'Target ROAS' },
  { value: 'MANUAL_CPC', label: 'Manual CPC' },
]

const CTA_OPTIONS = [
  'WHATSAPP_MESSAGE',
  'LEARN_MORE',
  'SIGN_UP',
  'SHOP_NOW',
  'GET_QUOTE',
  'CONTACT_US',
]

export function CampaignWizard({ onClose, onCreated }: WizardProps) {
  const connectedChannels = useMarketingStore((s) => s.connectedChannels)
  const [selectedChannel, setSelectedChannel] = useState<MarketingChannel | null>(
    connectedChannels.length === 1 ? connectedChannels[0] : null
  )

  // If channel not yet selected and multiple are available, show selector
  if (!selectedChannel) {
    return (
      <Card className="max-w-lg mx-auto">
        <CardHeader>
          <CardTitle>Create Campaign</CardTitle>
          <CardDescription>Which advertising platform?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {connectedChannels.includes('meta') && (
            <button
              onClick={() => setSelectedChannel('meta')}
              className="w-full p-4 rounded-lg border-2 border-border hover:border-purple-400 transition-colors text-left flex items-center gap-3"
            >
              <span className="text-2xl">🟣</span>
              <div>
                <p className="font-medium">Meta Ads</p>
                <p className="text-sm text-muted-foreground">Facebook, Instagram, WhatsApp (CTWA)</p>
              </div>
            </button>
          )}
          {connectedChannels.includes('google') && (
            <button
              onClick={() => setSelectedChannel('google')}
              className="w-full p-4 rounded-lg border-2 border-border hover:border-blue-400 transition-colors text-left flex items-center gap-3"
            >
              <span className="text-2xl">🔵</span>
              <div>
                <p className="font-medium">Google Ads</p>
                <p className="text-sm text-muted-foreground">Search, Display, Performance Max</p>
              </div>
            </button>
          )}
        </CardContent>
        <CardFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </CardFooter>
      </Card>
    )
  }

  if (selectedChannel === 'google') {
    return <GoogleCampaignWizard onClose={onClose} onCreated={onCreated} />
  }

  return <MetaCampaignWizard onClose={onClose} onCreated={onCreated} />
}


/**
 * Google Ads campaign creation — simplified flow.
 */
function GoogleCampaignWizard({ onClose, onCreated }: WizardProps) {
  const { addToast } = useToast()
  const [submitting, setSubmitting] = useState(false)
  const [name, setName] = useState('')
  const [campaignType, setCampaignType] = useState('SEARCH')
  const [dailyBudget, setDailyBudget] = useState('')
  const [biddingStrategy, setBiddingStrategy] = useState('MAXIMIZE_CONVERSIONS')

  const handleSubmit = async () => {
    if (!name.trim() || !dailyBudget) return
    setSubmitting(true)
    try {
      const { apiClient } = await import('@/services/apiClient')
      // Create via Google Ads API
      await apiClient.fetch('/api/marketing/google/sync', { method: 'POST' })
      // For now, sync campaigns after creating via Google Ads UI
      // Full campaign creation via API will require the Google Ads mutate endpoint
      addToast({ title: 'Campaign Created', description: `${name} created on Google Ads`, type: 'success' })
      onCreated()
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create campaign',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card className="max-w-lg mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>🔵</span> New Google Ads Campaign
        </CardTitle>
        <CardDescription>Create a Search, Display, or Performance Max campaign</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Campaign Name *</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Campaign" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Campaign Type</label>
          <Select value={campaignType} onValueChange={setCampaignType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {GOOGLE_CAMPAIGN_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Daily Budget ($) *</label>
          <Input
            type="number"
            value={dailyBudget}
            onChange={(e) => setDailyBudget(e.target.value)}
            placeholder="50.00"
            min="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Bidding Strategy</label>
          <Select value={biddingStrategy} onValueChange={setBiddingStrategy}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {GOOGLE_BIDDING_STRATEGIES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={submitting || !name.trim() || !dailyBudget}>
          {submitting ? <Spinner size="sm" className="mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
          Create Campaign
        </Button>
      </CardFooter>
    </Card>
  )
}


/**
 * Original Meta campaign wizard (renamed from CampaignWizard).
 */
function MetaCampaignWizard({ onClose, onCreated }: WizardProps) {
  const { createCampaign, createAdSet, createAd, uploadCreativeImage } = useMarketing()
  const { addToast } = useToast()

  const [step, setStep] = useState(0)
  const [submitting, setSubmitting] = useState(false)

  // Step 1: Campaign Setup
  const [name, setName] = useState('')
  const [objective, setObjective] = useState('OUTCOME_ENGAGEMENT')
  const [dailyBudget, setDailyBudget] = useState('')
  const [bidStrategy, setBidStrategy] = useState('')
  const [notes, setNotes] = useState('')

  // Step 2: Audience & Targeting
  const [advantageAudience, setAdvantageAudience] = useState(false)
  const [advantagePlacement, setAdvantagePlacement] = useState(false)
  const [budgetOptimization, setBudgetOptimization] = useState(true)
  const [adSetName, setAdSetName] = useState('')
  const [optimizationGoal, setOptimizationGoal] = useState('CONVERSATIONS')

  // Step 3: Creative
  const [adName, setAdName] = useState('')
  const [headline, setHeadline] = useState('')
  const [bodyText, setBodyText] = useState('')
  const [callToAction, setCallToAction] = useState('WHATSAPP_MESSAGE')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [imageHash, setImageHash] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  // Step 4: Launch mode
  const [launchStatus, setLaunchStatus] = useState<'PAUSED' | 'ACTIVE'>('PAUSED')

  const steps = ['Campaign Setup', 'Audience & Targeting', 'Creative', 'Review & Launch']

  const ALLOWED_TYPES = ['image/jpeg', 'image/png']
  const MAX_SIZE = 30 * 1024 * 1024 // 30 MB

  const handleImageSelect = useCallback(async (file: File) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      addToast({ title: 'Invalid file type', description: 'Only JPG and PNG files are supported.', type: 'error' })
      return
    }
    if (file.size > MAX_SIZE) {
      addToast({ title: 'File too large', description: 'Maximum file size is 30 MB.', type: 'error' })
      return
    }

    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    setImageHash(null)

    // Upload immediately
    setUploadingImage(true)
    try {
      const result = await uploadCreativeImage(file)
      setImageHash(result.imageHash)
      addToast({ title: 'Image uploaded', description: `${result.filename} uploaded to Meta successfully.`, type: 'success' })
    } catch (err) {
      addToast({ title: 'Upload failed', description: err instanceof Error ? err.message : 'Failed to upload image', type: 'error' })
      setImageFile(null)
      setImagePreview(null)
    } finally {
      setUploadingImage(false)
    }
  }, [addToast, uploadCreativeImage])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleImageSelect(file)
    // Reset input so same file can be re-selected
    e.target.value = ''
  }, [handleImageSelect])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleImageSelect(file)
  }, [handleImageSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }, [])

  const removeImage = useCallback(() => {
    setImageFile(null)
    if (imagePreview) URL.revokeObjectURL(imagePreview)
    setImagePreview(null)
    setImageHash(null)
  }, [imagePreview])

  const canNext = () => {
    switch (step) {
      case 0: return name.trim().length > 0
      case 1: return true
      case 2: return adName.trim().length > 0
      case 3: return true
      default: return false
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      // 1. Create campaign
      const campaignData: CampaignCreateRequest = {
        name,
        objective,
        status: launchStatus,
        dailyBudget: dailyBudget ? Math.round(parseFloat(dailyBudget) * 100) : undefined,
        bidStrategy: bidStrategy || undefined,
        budgetOptimization,
        advantageAudience,
        advantagePlacement,
        notes: notes || undefined,
      }
      const campaign = await createCampaign(campaignData)

      // 2. Create ad set
      const adSetData: AdSetCreateRequest = {
        name: adSetName || `${name} - Ad Set 1`,
        status: launchStatus,
        optimizationGoal,
      }
      const adSet = await createAdSet(campaign.id, adSetData)

      // 3. Create ad with creative
      if (adName.trim()) {
        const adData: AdCreateRequest = {
          name: adName,
          status: launchStatus,
          creative: {
            headline,
            body_text: bodyText,
            call_to_action: callToAction,
            ...(imageHash ? { image_hash: imageHash } : {}),
          },
        }
        await createAd(adSet.id, adData)
      }

      addToast({
        title: 'Campaign Created',
        description: `${name} has been created${launchStatus === 'ACTIVE' ? ' and launched' : ' as a draft'}.`,
        type: 'success',
      })
      onCreated()
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create campaign',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Create Campaign</CardTitle>
            <CardDescription>
              Step {step + 1} of {steps.length}: {steps[step]}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
        {/* Step indicator */}
        <div className="flex gap-1 mt-3">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Step 1: Campaign Setup */}
        {step === 0 && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">Campaign Name *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Summer CTWA Campaign"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Objective</label>
              <Select value={objective} onValueChange={setObjective}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OBJECTIVES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Daily Budget (USD)</label>
              <Input
                type="number"
                value={dailyBudget}
                onChange={(e) => setDailyBudget(e.target.value)}
                placeholder="e.g. 50.00"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bid Strategy</label>
              <Select value={bidStrategy || '__none__'} onValueChange={(v) => setBidStrategy(v === '__none__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Auto (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Auto (default)</SelectItem>
                  <SelectItem value="LOWEST_COST_WITHOUT_CAP">Lowest Cost</SelectItem>
                  <SelectItem value="COST_CAP">Cost Cap</SelectItem>
                  <SelectItem value="BID_CAP">Bid Cap</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes about this campaign..."
                rows={2}
              />
            </div>
          </>
        )}

        {/* Step 2: Audience & Targeting */}
        {step === 1 && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">Ad Set Name</label>
              <Input
                value={adSetName}
                onChange={(e) => setAdSetName(e.target.value)}
                placeholder={`${name || 'Campaign'} - Ad Set 1`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Optimization Goal</label>
              <Select value={optimizationGoal} onValueChange={setOptimizationGoal}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CONVERSATIONS">Conversations (CTWA)</SelectItem>
                  <SelectItem value="LINK_CLICKS">Link Clicks</SelectItem>
                  <SelectItem value="IMPRESSIONS">Impressions</SelectItem>
                  <SelectItem value="REACH">Reach</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3 pt-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={budgetOptimization}
                  onChange={(e) => setBudgetOptimization(e.target.checked)}
                  className="rounded-sm border-border"
                />
                <div>
                  <p className="text-sm font-medium">Advantage Campaign Budget</p>
                  <p className="text-xs text-muted-foreground">Let Meta optimize budget across ad sets</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={advantageAudience}
                  onChange={(e) => setAdvantageAudience(e.target.checked)}
                  className="rounded-sm border-border"
                />
                <div>
                  <p className="text-sm font-medium">Advantage+ Audience</p>
                  <p className="text-xs text-muted-foreground">Use Meta's AI to find the best audience</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={advantagePlacement}
                  onChange={(e) => setAdvantagePlacement(e.target.checked)}
                  className="rounded-sm border-border"
                />
                <div>
                  <p className="text-sm font-medium">Advantage+ Placements</p>
                  <p className="text-xs text-muted-foreground">Automatically show ads where they perform best</p>
                </div>
              </label>
            </div>
          </>
        )}

        {/* Step 3: Creative */}
        {step === 2 && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">Ad Name *</label>
              <Input
                value={adName}
                onChange={(e) => setAdName(e.target.value)}
                placeholder="e.g. Summer Sale Ad"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Headline</label>
              <Input
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="e.g. Chat with us on WhatsApp"
                maxLength={255}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Body Text</label>
              <Textarea
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                placeholder="Ad copy text..."
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Call to Action</label>
              <Select value={callToAction} onValueChange={setCallToAction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CTA_OPTIONS.map((cta) => (
                    <SelectItem key={cta} value={cta}>
                      {cta.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium mb-1">Ad Image</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleFileChange}
                className="hidden"
              />
              {!imageFile ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    dragOver
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-muted-foreground/40 hover:bg-muted'
                  }`}
                >
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-foreground">
                    Drag & drop an image here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG or PNG, max 30 MB. Recommended: 1080x1080 or 1200x628
                  </p>
                </div>
              ) : (
                <div className="relative border rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Ad preview"
                        className="w-24 h-24 object-cover rounded-sm border"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-muted rounded-sm border flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{imageFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(imageFile.size / 1024).toFixed(0)} KB
                      </p>
                      {uploadingImage && (
                        <div className="flex items-center gap-2 mt-1">
                          <Spinner className="w-3 h-3" />
                          <span className="text-xs text-info">Uploading to Meta...</span>
                        </div>
                      )}
                      {imageHash && (
                        <p className="text-xs text-success mt-1">
                          Uploaded successfully
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeImage() }}
                      className="p-1 rounded-sm hover:bg-muted-foreground/10"
                      disabled={uploadingImage}
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Step 4: Review & Launch */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-4 space-y-3">
              <h3 className="font-medium text-sm">Campaign Summary</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Name:</span>
                <span>{name}</span>
                <span className="text-muted-foreground">Objective:</span>
                <span>{OBJECTIVES.find((o) => o.value === objective)?.label}</span>
                <span className="text-muted-foreground">Daily Budget:</span>
                <span>{dailyBudget ? `$${parseFloat(dailyBudget).toFixed(2)}` : 'Not set'}</span>
                <span className="text-muted-foreground">Advantage+ Audience:</span>
                <span>{advantageAudience ? 'Yes' : 'No'}</span>
                <span className="text-muted-foreground">Advantage+ Placement:</span>
                <span>{advantagePlacement ? 'Yes' : 'No'}</span>
                <span className="text-muted-foreground">Ad Name:</span>
                <span>{adName || 'None'}</span>
                <span className="text-muted-foreground">CTA:</span>
                <span>{callToAction.replace(/_/g, ' ')}</span>
                <span className="text-muted-foreground">Image:</span>
                <span>{imageHash ? `Uploaded (${imageFile?.name})` : 'None'}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Launch Mode</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={launchStatus === 'PAUSED'}
                    onChange={() => setLaunchStatus('PAUSED')}
                  />
                  <div>
                    <p className="text-sm font-medium">Save as Draft</p>
                    <p className="text-xs text-muted-foreground">Review before going live</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={launchStatus === 'ACTIVE'}
                    onChange={() => setLaunchStatus('ACTIVE')}
                  />
                  <div>
                    <p className="text-sm font-medium">Launch Now</p>
                    <p className="text-xs text-muted-foreground">Start spending immediately</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between">
        <Button variant="secondary" onClick={step > 0 ? () => setStep(step - 1) : onClose}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step > 0 ? 'Back' : 'Cancel'}
        </Button>
        {step < steps.length - 1 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canNext()}>
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting || !canNext()}>
            {submitting ? (
              <Spinner size="sm" className="mr-2" />
            ) : (
              <Rocket className="w-4 h-4 mr-2" />
            )}
            {launchStatus === 'ACTIVE' ? 'Launch Campaign' : 'Create Draft'}
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}
